import { useState, useEffect } from 'react'
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Handshake,
  LayoutDashboard,
  Link2,
  Users,
  CreditCard,
  Wallet,
  FileText,
  Settings,
  ChevronLeft,
  ChevronRight,
  LogOut,
  User,
  Bell,
  Crown,
  UsersRound,
  UserPlus,
  BarChart3,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { PartnerPortalService, type PartnerUser } from '@/domain/partner-portal'

// ============================================
// Feature Status Config (เหมือน Admin UI)
// ============================================
type FeatureStatus = 'ready' | 'dev'

const IS_DEV_MODE = 
  import.meta.env.VITE_DEV_MODE === 'true' || 
  import.meta.env.DEV === true

interface NavItem {
  title: string
  href: string
  icon: React.ReactNode
  badge?: number
  status?: FeatureStatus  // 'ready' = พร้อมใช้, 'dev' = กำลังพัฒนา
  leaderOnly?: boolean    // แสดงเฉพาะ Leader
}

// เมนูสำหรับ Partner ทั่วไป
const partnerNavItems: NavItem[] = [
  {
    title: 'แดชบอร์ด',
    href: '/affiliate',
    icon: <LayoutDashboard className="h-5 w-5" />,
    status: 'ready',
  },
  {
    title: 'ลิงก์แนะนำ',
    href: '/affiliate/links',
    icon: <Link2 className="h-5 w-5" />,
    status: 'dev',
  },
  {
    title: 'สมาชิกที่แนะนำ',
    href: '/affiliate/referrals',
    icon: <Users className="h-5 w-5" />,
    status: 'ready',
  },
  {
    title: 'คอมมิชชั่น',
    href: '/affiliate/commissions',
    icon: <CreditCard className="h-5 w-5" />,
    status: 'dev',
  },
  {
    title: 'ถอนเงิน',
    href: '/affiliate/withdraw',
    icon: <Wallet className="h-5 w-5" />,
    status: 'dev',
  },
  {
    title: 'รายงาน',
    href: '/affiliate/reports',
    icon: <FileText className="h-5 w-5" />,
    status: 'ready',
  },
  {
    title: 'ตั้งค่า',
    href: '/affiliate/settings',
    icon: <Settings className="h-5 w-5" />,
    status: 'dev',
  },
]

// เมนูเพิ่มเติมสำหรับ Leader (admin_group = 4)
const leaderNavItems: NavItem[] = [
  {
    title: 'ภาพรวมทีม',
    href: '/affiliate/team',
    icon: <UsersRound className="h-5 w-5" />,
    status: 'dev',
    leaderOnly: true,
  },
  {
    title: 'จัดการทีม',
    href: '/affiliate/team/manage',
    icon: <UserPlus className="h-5 w-5" />,
    status: 'dev',
    leaderOnly: true,
  },
  {
    title: 'รายงานทีม',
    href: '/affiliate/team/reports',
    icon: <BarChart3 className="h-5 w-5" />,
    status: 'dev',
    leaderOnly: true,
  },
]

// Function to get nav items based on user role
const getNavItems = (isLeader: boolean): NavItem[] => {
  let items = [...partnerNavItems]
  
  // เพิ่มเมนู Leader หลังแดชบอร์ด
  if (isLeader) {
    items = [
      items[0], // แดชบอร์ด
      ...leaderNavItems,
      ...items.slice(1), // เมนูที่เหลือ
    ]
  }
  
  // Filter dev items ตอน production
  if (!IS_DEV_MODE) {
    items = items.filter(item => item.status !== 'dev')
  }
  
  return items
}

export function AffiliateLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const [collapsed, setCollapsed] = useState(false)
  const [user, setUser] = useState<PartnerUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const checkSession = async () => {
      // ตรวจสอบ session และ restore user
      const partnerUser = await PartnerPortalService.validateAndRestoreSession()
      
      if (!partnerUser) {
        navigate('/affiliate/login')
        return
      }

      setUser(partnerUser)
      setIsLoading(false)
    }
    
    checkSession()
  }, [navigate])

  const handleLogout = () => {
    PartnerPortalService.logout()
    navigate('/affiliate/login')
  }

  const isActive = (href: string) => {
    if (href === '/affiliate') {
      return location.pathname === href
    }
    return location.pathname.startsWith(href)
  }

  if (isLoading || !user) return null
  
  // Helper to get display name
  const displayName = user.firstname 
    ? `${user.firstname} ${user.lastname || ''}`.trim()
    : user.username
  
  // Partner level display (admin_group 3 = Partner, 4 = Partner Leader)
  const isLeader = user.admin_group === 4
  const partnerLevel = isLeader ? 'Leader' : 'Partner'
  
  // Get nav items based on role
  const navItems = getNavItems(isLeader)

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar */}
      <aside
        className={cn(
          "fixed left-0 top-0 z-40 h-screen border-r border-border bg-card transition-all duration-300",
          collapsed ? "w-16" : "w-64"
        )}
      >
        {/* Logo */}
        <div className={cn(
          "flex h-16 items-center border-b border-border px-4",
          collapsed ? "justify-center" : "gap-3"
        )}>
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-sm">
            <Handshake className="h-6 w-6" />
          </div>
          {!collapsed && (
            <div>
              <h1 className="text-lg font-bold text-foreground">
                Affiliate
              </h1>
              <p className="text-xs text-muted-foreground">Portal</p>
            </div>
          )}
        </div>

        {/* User Info */}
        {!collapsed && (
          <div className="p-4 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                <User className="h-5 w-5 text-muted-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{displayName}</p>
                <div className="flex items-center gap-1">
                  <Badge className={cn("text-[10px] px-1.5 py-0 text-white border-0", user.admin_group === 4 ? 'bg-purple-500' : 'bg-indigo-500')}>
                    <Crown className="h-2.5 w-2.5 mr-0.5" />
                    {partnerLevel}
                  </Badge>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <ScrollArea className="h-[calc(100vh-12rem)] px-3 py-4">
          <nav className="space-y-1">
            {navItems.map((item) => {
              const active = isActive(item.href)
              const content = (
                <Link
                  to={item.href}
                  className={cn(
                    "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                    "hover:bg-accent hover:text-accent-foreground",
                    active && "bg-primary text-primary-foreground",
                    !active && "text-muted-foreground",
                    collapsed && "justify-center px-2"
                  )}
                >
                  <span className={cn(
                    "flex items-center justify-center shrink-0",
                    active ? "text-primary-foreground" : "text-muted-foreground"
                  )}>
                    {item.icon}
                  </span>
                  {!collapsed && (
                    <>
                      <span className="flex-1 truncate">{item.title}</span>
                      {/* DEV Badge - แสดงเฉพาะ dev mode */}
                      {IS_DEV_MODE && item.status === 'dev' && (
                        <Badge 
                          variant="outline" 
                          className="ml-auto text-[10px] px-1.5 py-0 bg-blue-500/10 text-blue-500 border-blue-500/30"
                        >
                          DEV
                        </Badge>
                      )}
                      {item.badge && (
                        <Badge 
                          variant="secondary" 
                          className="ml-auto text-[10px] px-1.5 py-0 bg-primary/10 text-primary"
                        >
                          {item.badge}
                        </Badge>
                      )}
                    </>
                  )}
                </Link>
              )

              if (collapsed) {
                return (
                  <Tooltip key={item.href} delayDuration={0}>
                    <TooltipTrigger asChild>
                      {content}
                    </TooltipTrigger>
                    <TooltipContent side="right" className="flex items-center gap-2">
                      {item.title}
                      {item.badge && <Badge variant="secondary">{item.badge}</Badge>}
                    </TooltipContent>
                  </Tooltip>
                )
              }

              return <div key={item.href}>{content}</div>
            })}
          </nav>
        </ScrollArea>

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-border p-3">
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "w-full justify-start text-muted-foreground hover:text-foreground hover:bg-accent",
              collapsed && "justify-center"
            )}
            onClick={() => setCollapsed(!collapsed)}
          >
            {collapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <>
                <ChevronLeft className="h-4 w-4 mr-2" />
                <span>ย่อเมนู</span>
              </>
            )}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <div className={cn(
        "transition-all duration-300",
        collapsed ? "ml-16" : "ml-64"
      )}>
        {/* Header */}
        <header className="sticky top-0 z-30 h-16 border-b border-border bg-card/80 backdrop-blur">
          <div className="flex h-full items-center justify-between px-6">
            <div>
              <h2 className="text-lg font-semibold">ยินดีต้อนรับ, {displayName}</h2>
              <p className="text-xs text-muted-foreground">รหัสพันธมิตร: {user.objectId.slice(-8).toUpperCase()}</p>
            </div>

            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" className="relative">
                <Bell className="h-5 w-5" />
                <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-destructive" />
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <User className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="p-2">
                    <p className="text-sm font-medium">{displayName}</p>
                    <p className="text-xs text-muted-foreground">{user.email || user.username}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/affiliate/settings" className="gap-2">
                      <Settings className="h-4 w-4" />
                      ตั้งค่าบัญชี
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="gap-2 text-destructive">
                    <LogOut className="h-4 w-4" />
                    ออกจากระบบ
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}















